import { JSXElementConstructor } from 'react';

import { CellContext, createColumnHelper } from '@tanstack/react-table';

import { RowData } from '../../../src/utils';
import { RowActionMenu } from '../components/row-action/RowActionMenu/RowActionMenu';
import { ACTIONS_MENU_COLUMN_ID } from '../config/constants';
import { ActionColumnProps } from '../types/ActionColumn';
import type { CreateTableMeta } from './createTable.types';

/**
 * Creates a row selection column that is by default not printable
 *
 * This can be over ridden when instantiating the column and using the meta property on the args
 */
function createActionsColumn<
  TData extends RowData,
  TMeta extends Record<string, unknown> = Record<string, unknown>,
  TReactTable = CreateTableMeta<TData, TMeta>,
  TCellContext = CellContext<TData, unknown> & { table: TReactTable },
>() {
  const columnHelper = createColumnHelper<TData>();
  type Accessor = typeof columnHelper.accessor;
  type TableMeta = Parameters<Accessor>[1]['meta'];
  type ColumnArgs = {
    meta?: TableMeta;
    Actions: JSXElementConstructor<ActionColumnProps<TData>>;
    /**
     * Determines if the actions column should be visible for the row
     * This is useful for when the actions column should be hidden for certain rows
     * @default () => true - always show the actions column
     */
    isColumnVisible?: (cellContext: TCellContext) => boolean;
  };

  const actionsColumn = ({ meta, Actions, isColumnVisible = () => true }: ColumnArgs) =>
    columnHelper.display({
      size: 45,
      id: ACTIONS_MENU_COLUMN_ID,
      cell: (cellContext) => {
        const _isColumnVisible = isColumnVisible(cellContext as TCellContext);
        if (!_isColumnVisible) return null;
        return <RowActionMenu Actions={Actions} cellContext={cellContext} />;
      },
      meta: {
        isPrintable: false,
        ...meta,
      },
    });

  return actionsColumn;
}

export { createActionsColumn };
